import { Typography } from '../../components/ui/Typography';
import { Button } from '../../components/ui/Button';
import { ImgWithSkeleton } from '../../components/ui/ImgWithSkeleton';
import { FavoriteButton } from '../../components/FavoriteButton';
import { formatWineType } from './utils';
import type { PopulatedWine } from './types';
import type { Media } from '@wineryxplorer/shared';
import { trpc } from '../../lib/trpc';
import { Link } from 'react-router-dom';

interface WineHeaderProps {
  wine: PopulatedWine;
  wineImages?: Media[];
  heroImageData?: Media;
  onEditClick?: () => void;
  showEditButton?: boolean;
  showScoresAndDescriptors?: boolean;
}

export const WineHeader = ({ wine, wineImages = [], heroImageData, onEditClick, showEditButton = false, showScoresAndDescriptors = true }: WineHeaderProps) => {
  const tastingsQuery = trpc.wine.tastingsForWine.useQuery(
    { wineId: wine._id },
    { enabled: Boolean(wine._id) }
  );
  const tastings = tastingsQuery.data ?? [];

  // Use directly fetched hero image data if available, otherwise try to find in wineImages
  const heroImageMedia = heroImageData || wineImages.find(media => media.id === wine.heroImageId);

  return (
    <div className="border-b border-gray-200 pb-4 md:pb-6 lg:pb-8">
      <div className="lg:flex lg:items-start lg:justify-between lg:gap-8">
        <div className="lg:flex-1">
          {/* Header with Edit Button */}
          <div className="flex justify-between items-start mb-3 sm:mb-4 md:mb-3 lg:mb-4">
            <div className="flex-1">
              {/* Winery name as H1 with link - Mobile optimized touch target */}
              {wine.venueId?.name ? (
                <Typography 
                  variant="heading1" 
                  as="h1"
                >
                  <a 
                    href={`/wineries/${wine.venueId._id}`}
                    className="text-primary-600 hover:text-primary-800 hover:underline transition-colors touch-manipulation inline-block py-1 -mx-1 px-1 rounded focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2"
                    aria-label={`Visit ${wine.venueId.name} winery page`}
                  >
                    {wine.venueId.name}
                  </a>
                </Typography>
              ) : (
                <div className="h-10 sm:h-12 md:h-16 lg:h-20 w-3/4 bg-gray-200 rounded animate-pulse"></div>
              )}
            </div>
            
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              <FavoriteButton entityType="wine" entityId={wine._id} />
              {/* Edit button - only show if user can edit this wine */}
              {showEditButton && onEditClick && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={onEditClick}
                >
                  Edit Wine
                </Button>
              )}
            </div>
          </div>
          
          {/* Wine name as H2 - Mobile optimized spacing */}
          <Typography
            variant="heading2"
            as="h2"
            className="mb-3 sm:mb-4 md:mb-3 lg:mb-4"
          >
            {wine.name}
          </Typography>

          {/* Poured on badges */}
          {tastings.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="font-sans text-xs uppercase tracking-widest text-gray-600 mr-1 self-center">
                Poured on
              </span>
              {tastings.map((t) => (
                <Link
                  key={t._id}
                  to={`/tasting-experiences/${t._id}`}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-50 text-secondary-800 border border-secondary-200 hover:bg-secondary-100"
                >
                  {t.name}
                </Link>
              ))}
            </div>
          )}

          {/* Wine type as H3 - Mobile optimized spacing */}
          <Typography 
            variant="heading3" 
            as="h3" 
            className="mb-4 sm:mb-5 md:mb-6 lg:mb-8 text-gray-700"
          >
            {formatWineType(wine.wineType)}
          </Typography>
          
          {/* Additional wine information - Mobile optimized layout */}
          <div className="sm:space-y-4 md:space-y-3 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-4 lg:space-y-0 mb-6 lg:mb-8">
            {wine.vintage && (
              <div className="flex items-center gap-3 sm:gap-4 md:gap-3 lg:gap-4 py-1">
                <Typography 
                  variant="label" 
                  as="span" 
                  className="min-w-0 flex-shrink-0 text-gray-600"
                >
                  Vintage:
                </Typography>
                <Typography 
                  variant="wineVintage" 
                  as="span"
                  className="font-medium"
                >
                  {wine.vintage}
                </Typography>
              </div>
            )}
            {wine.price && (
              <div className="flex items-center gap-3 sm:gap-4 md:gap-3 lg:gap-4 py-1">
                <Typography 
                  variant="label" 
                  as="span" 
                  className="min-w-0 flex-shrink-0 text-gray-600"
                >
                  Price:
                </Typography>
                <Typography 
                  variant="bodyMedium" 
                  as="span" 
                  className="text-green-600 font-semibold"
                >
                  ${wine.price.toFixed(2)}
                </Typography>
              </div>
            )}
          </div>

          {/* Description Section - Hidden on mobile, shown on large screens */}
          <div className="hidden lg:block">
            <Typography 
              variant="heading3" 
              as="h2" 
              className="mb-3 md:mb-4 lg:mb-6"
            >
              Description
            </Typography>
            {wine.description ? (
              <Typography 
                variant="bodyLarge" 
                as="p"
              >
                {wine.description}
              </Typography>
            ) : (
              <Typography 
                variant="bodyLarge" 
                as="p" 
                className="text-gray-500 italic"
              >
                No description available
              </Typography>
            )}

            {/* Professional Scores and Descriptors - Below description on desktop */}
            {showScoresAndDescriptors && (
              <div className="mt-8 space-y-6">
                {/* Professional Scores */}
                {wine.professionalScores.length > 0 && (
                  <div>
                    <Typography 
                      variant="heading3" 
                      as="h3" 
                      className="mb-3"
                    >
                      Professional Scores
                    </Typography>
                    <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th 
                              scope="col" 
                              className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Reviewer
                            </th>
                            <th 
                              scope="col" 
                              className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Score
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-gray-50 divide-y divide-gray-200">
                          {wine.professionalScores.map((scoreEntry, index) => {
                            const reviewer = Object.keys(scoreEntry)[0];
                            const score = scoreEntry[reviewer];
                            
                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <Typography 
                                    variant="bodyMedium" 
                                    as="div"
                                    className="text-gray-900 font-medium text-sm"
                                  >
                                    {reviewer}
                                  </Typography>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <Typography 
                                    variant="bodyLarge" 
                                    as="div"
                                    className="text-primary-600 font-semibold"
                                  >
                                    {score}
                                  </Typography>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Descriptors */}
                {wine.descriptors.length > 0 && (
                  <div>
                    <Typography 
                      variant="heading3" 
                      as="h3" 
                      className="mb-3"
                    >
                      Descriptors
                    </Typography>
                    <div className="flex flex-wrap gap-2">
                      {wine.descriptors.map((descriptor, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-primary-100 text-primary-800 border border-primary-200"
                        >
                          {descriptor}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Hero Image Section - Enhanced responsive positioning */}
        <div className="mt-6 sm:mt-8 md:mt-6 lg:mt-0 lg:w-80 xl:w-96 2xl:w-[400px] flex-shrink-0">
          {/* Mobile: Center the image with optimal sizing, Desktop: Right column */}
          <div className="mx-auto max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-none">
            <div className="aspect-[9/16] bg-gray-100 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200">
              <ImgWithSkeleton
                src={heroImageMedia ? heroImageMedia.cloudinaryUrl : '/assets/placeholders/placeholder-bottle-portrait.png'}
                alt={`${wine.name} hero image`}
                fallbackSrc="/assets/placeholders/placeholder-bottle-portrait.png"
                className="w-full h-full object-contain"
                skeletonClassName="rounded-lg"
              />
            </div>

            {/* Professional Scores and Descriptors removed from here - now in left column */}
          </div>
        </div>
      </div>
    </div>
  );
};